import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Building2, Handshake, CheckCircle2, Loader2, Sparkles, Info,
} from "lucide-react";

/**
 * Fase 14.6 · Form B2B/B2B2C que crea un demo_request público.
 * Query param `?type=enterprise|partner` cambia el copy.
 */
export default function DemoRequest() {
  const [sp] = useSearchParams();
  const [type, setType] = useState(sp.get("type") || "enterprise");
  const [form, setForm] = useState({
    name: "", work_email: "", company: "", role: "",
    phone: "", employees_range: "", use_case: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const paramType = sp.get("type");
    if (paramType && ["enterprise", "partner"].includes(paramType)) {
      setType(paramType);
    }
  }, [sp]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.work_email.trim() || !form.company.trim() || !form.role.trim()) {
      toast.error("Completa los campos obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/demo-requests", {
        ...form,
        type,
        captcha_token: "demo-mode",
      });
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isPartner = type === "partner";
  const heroTitle = isPartner
    ? "Lanza tu academia de finanzas white-label"
    : "Capacita a tu equipo con la infraestructura DCC";
  const heroDesc = isPartner
    ? "Universidades, asociaciones, colegios profesionales y consultoras: usa nuestra infraestructura con tu marca y comparte revenue con nosotros."
    : "Bancos, sofipos, casas de bolsa, aseguradoras y fintechs: licencias por asientos con onboarding masivo, reportería ejecutiva y certificados verificables.";

  const pageTitle = isPartner
    ? "Alianza white-label · DCC"
    : "Demo B2B · DCC";

  return (
    <PublicLayout>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={heroDesc} />
      </Helmet>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1">
                {isPartner ? <Handshake className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                {isPartner ? "Alianza white-label" : "Demo institucional"}
              </span>
              <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 font-display leading-tight">
                {heroTitle}
              </h1>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                {heroDesc}
              </p>

              <div className="mt-8 flex gap-2" data-testid="demo-type-tabs">
                <TypeTab active={!isPartner} onClick={() => setType("enterprise")}
                          testid="demo-type-enterprise">
                  <Building2 className="w-3.5 h-3.5" /> Institución
                </TypeTab>
                <TypeTab active={isPartner} onClick={() => setType("partner")}
                          testid="demo-type-partner">
                  <Handshake className="w-3.5 h-3.5" /> Aliado
                </TypeTab>
              </div>

              <div className="mt-8 space-y-3">
                <Bullet>Respuesta en 24-48 horas hábiles</Bullet>
                <Bullet>Sesión de 30 min sin compromiso</Bullet>
                <Bullet>Propuesta con casos comparables</Bullet>
                <Bullet>NDA disponible antes de compartir datos sensibles</Bullet>
              </div>

              <div className="mt-10 p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="demo-mock-note">
                <p className="text-xs text-amber-900 flex items-start gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>MOCKED</strong> · Este form persiste en la DB pero la notificación al
                    equipo comercial no se envía por email. Un superadmin puede ver las solicitudes en
                    <code className="mx-1 px-1 bg-amber-100 border border-amber-300 rounded font-mono text-[11px]">/app/superadmin/demo-requests</code>.
                  </span>
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              {success ? (
                <div
                  data-testid="demo-success"
                  className="p-10 rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 text-center"
                >
                  <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
                  <h2 className="mt-6 text-2xl font-bold text-slate-900 font-display">
                    Solicitud recibida
                  </h2>
                  <p className="mt-3 text-base text-slate-700 max-w-md mx-auto">
                    Recibimos tu solicitud. Nuestro equipo comercial te contactará en
                    <strong> 24-48 horas hábiles </strong> al correo <strong>{form.work_email}</strong>.
                  </p>
                  <p className="mt-4 text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
                    MOCKED · No se envió email real
                  </p>
                  <div className="mt-8 flex justify-center gap-3">
                    <Link to="/" className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-primary hover:brightness-110 text-white font-semibold text-sm">
                      Volver al inicio
                    </Link>
                    <Link to="/catalogo" className="inline-flex items-center gap-1 px-5 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-white font-semibold text-sm">
                      Explorar catálogo <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="demo-form"
                  className="p-6 sm:p-8 rounded-2xl border border-slate-200 bg-white shadow-sm space-y-5"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nombre completo *" testid="demo-name">
                      <input required value={form.name} onChange={set("name")}
                              data-testid="demo-input-name"
                              placeholder="Ana Rivera"
                              className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-primary" />
                    </Field>
                    <Field label="Email corporativo *" testid="demo-email">
                      <input required type="email" value={form.work_email} onChange={set("work_email")}
                              data-testid="demo-input-email"
                              placeholder="ana@banco.mx"
                              className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-primary" />
                    </Field>
                    <Field label="Empresa *" testid="demo-company">
                      <input required value={form.company} onChange={set("company")}
                              data-testid="demo-input-company"
                              placeholder="Banco XYZ"
                              className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-primary" />
                    </Field>
                    <Field label="Cargo *" testid="demo-role">
                      <input required value={form.role} onChange={set("role")}
                              data-testid="demo-input-role"
                              placeholder="Directora de Talento"
                              className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-primary" />
                    </Field>
                    <Field label="Teléfono" testid="demo-phone">
                      <input value={form.phone} onChange={set("phone")}
                              data-testid="demo-input-phone"
                              placeholder="+52 55 1234 5678"
                              className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-primary" />
                    </Field>
                    <Field label="Tamaño de equipo" testid="demo-employees">
                      <select value={form.employees_range} onChange={set("employees_range")}
                                data-testid="demo-input-employees"
                                className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900">
                        <option value="">Selecciona…</option>
                        <option value="11-50">11 - 50</option>
                        <option value="51-200">51 - 200</option>
                        <option value="201-1000">201 - 1,000</option>
                        <option value="1000+">Más de 1,000</option>
                      </select>
                    </Field>
                  </div>
                  <Field label="¿Qué te interesa lograr?" testid="demo-usecase">
                    <select value={form.use_case} onChange={set("use_case")}
                              data-testid="demo-input-usecase"
                              className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900">
                      <option value="">Selecciona…</option>
                      <option value="capacitar_equipo">Capacitar a mi equipo</option>
                      <option value="lanzar_academia_whitelabel">Lanzar academia white-label</option>
                      <option value="integrar_api">Integrar API en mi plataforma</option>
                      <option value="otro">Otro</option>
                    </select>
                  </Field>
                  <Field label="Cuéntanos más" testid="demo-message">
                    <textarea value={form.message} onChange={set("message")}
                                data-testid="demo-input-message"
                                rows={4}
                                placeholder="Contexto, timing, objetivos, retos, tamaño de audiencia…"
                                className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-primary resize-y" />
                  </Field>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="demo-form-submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-md bg-primary hover:brightness-110 text-white font-bold text-sm transition-all disabled:opacity-60 shadow-lg shadow-primary/30"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {submitting ? "Enviando…" : "Enviar solicitud"}
                  </button>
                  <p className="text-[11px] text-slate-500 text-center">
                    Al enviar aceptas nuestros{" "}
                    <Link to="/placeholder" className="underline hover:text-primary">Términos</Link>{" "}
                    y{" "}
                    <Link to="/placeholder" className="underline hover:text-primary">Aviso de privacidad</Link>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function TypeTab({ active, onClick, children, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
        active
          ? "bg-primary border-primary text-white"
          : "bg-white border-slate-300 text-slate-600 hover:border-primary"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">{label}</span>
      {children}
    </label>
  );
}

function Bullet({ children }) {
  return (
    <p className="flex items-start gap-2 text-sm text-slate-700">
      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </p>
  );
}
